import React, { useEffect, useState } from 'react';
import { localeSwitcherShortLabel } from '@vastra/constants';
import {
  readLocale,
  subscribeLocale,
  subscribeStorefrontLocalesConfigured,
  writeLocale,
  getStorefrontLocalesForSwitcher,
} from '../lib/locale';
import { t } from '../lib/i18n';

/**
 * Compact locale switcher for the storefront header.
 * Reads/writes the persisted locale; `App.tsx` reloads i18n on change.
 * Only shown if multiLanguageEnabled is true and there are multiple languages available.
 */
const LocaleSwitcher: React.FC<{ className?: string; multiLanguageEnabled?: boolean }> = ({
  className,
  multiLanguageEnabled = false,
}) => {
  const [locale, setLocale] = useState(readLocale);
  const [options, setOptions] = useState(getStorefrontLocalesForSwitcher);

  useEffect(() => {
    setLocale(readLocale());
    return subscribeLocale((next) => setLocale(next));
  }, []);

  useEffect(() => {
    const refresh = () => setOptions(getStorefrontLocalesForSwitcher());
    refresh();
    return subscribeStorefrontLocalesConfigured(refresh);
  }, []);

  const onChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const next = event.target.value;
    if (next === locale) return;
    writeLocale(next);
  };

  if (!multiLanguageEnabled || options.length < 2) {
    return null;
  }

  return (
    <label
      className={`flex items-center gap-1 text-xs ${className ?? ''}`}
      aria-label={t('i18n.customer.navigation.en.locale.ariaLabel', 'Language')}
    >
      <span className="sr-only">{t('i18n.customer.navigation.en.locale.label', 'Language')}</span>
      <select
        value={locale}
        onChange={onChange}
        className="bg-transparent border border-stone-200 rounded-md px-1.5 py-1 text-xs font-medium text-stone-700 hover:border-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-400/30 cursor-pointer"
      >
        {options.map((opt) => (
          <option key={opt.code} value={opt.code}>
            {localeSwitcherShortLabel(opt)} · {opt.nativeLabel?.trim() || opt.label}
          </option>
        ))}
      </select>
    </label>
  );
};

export default LocaleSwitcher;
